import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { AuthBrandPanel, BrandMark } from '../../components/AuthBrandPanel';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../../components/ui/form';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { CheckCircle, Mail } from 'lucide-react';
import { config } from '../../config';

const resetPasswordSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
});

type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;

const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const token = searchParams.get('token');
    if (token) {
      navigate(`/auth/reset-password-confirm?token=${token}`, { replace: true });
    }
  }, [searchParams, navigate]);

  const form = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      email: '',
    },
  });

  const handleResetPassword = async (data: ResetPasswordFormData) => {
    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`${config.API_URL}/v1/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: data.email }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to send reset email');
      }

      setIsSuccess(true);
    } catch (err) {
      console.error('Password reset error:', err);
      setIsSuccess(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white lg:flex">
      <AuthBrandPanel />

      <div className="flex-1 bg-figma-bg-main">
        <div className="mx-auto flex min-h-screen w-full max-w-md flex-col px-4 py-6 sm:px-6 lg:py-8">
          <div className="flex items-center justify-between gap-4">
            <Link to="/" className="flex items-center gap-3 lg:hidden">
              <BrandMark />
              <div>
                <p className="text-lg font-semibold text-figma-text-primary">Good Outcomes</p>
                <p className="text-sm text-figma-text-secondary">Reset password</p>
              </div>
            </Link>

            <Button
              asChild
              variant="ghost"
              className="ml-auto text-primary hover:bg-primary/5 hover:text-primary"
            >
              <Link to="/auth">Back to sign in</Link>
            </Button>
          </div>

          <div className="my-auto py-8">
            <Card className="border-border bg-card shadow-sm">
              {isSuccess ? (
                <CardContent className="space-y-6 pt-10 pb-8 text-center">
                  <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10">
                    <CheckCircle className="h-8 w-8 text-primary" />
                  </div>
                  <div className="space-y-2">
                    <CardTitle className="text-2xl font-semibold">Check your email</CardTitle>
                    <CardDescription className="text-base leading-7">
                      If an account exists with that email address, we&apos;ve sent reset
                      instructions.
                    </CardDescription>
                  </div>
                  <div className="space-y-3">
                    <Button asChild className="w-full" size="lg">
                      <Link to="/auth">Back to sign in</Link>
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => {
                        setIsSuccess(false);
                        form.reset();
                      }}
                      className="w-full"
                    >
                      Try a different email
                    </Button>
                  </div>
                </CardContent>
              ) : (
                <>
                  <CardHeader className="space-y-3 text-center">
                    <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10">
                      <Mail className="h-8 w-8 text-primary" />
                    </div>
                    <CardTitle className="text-2xl font-semibold">Reset password</CardTitle>
                    <CardDescription className="mx-auto max-w-sm text-base leading-7">
                      Enter your email address and we&apos;ll send you a link to reset your
                      password.
                    </CardDescription>
                  </CardHeader>

                  <CardContent>
                    {error && (
                      <Alert variant="destructive" className="mb-6">
                        <AlertDescription>{error}</AlertDescription>
                      </Alert>
                    )}

                    <Form {...form}>
                      <form onSubmit={form.handleSubmit(handleResetPassword)} className="space-y-6">
                        <FormField
                          control={form.control}
                          name="email"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Email address</FormLabel>
                              <FormControl>
                                <Input type="email" placeholder="your.email@example.com" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <Button type="submit" className="w-full" disabled={isLoading} size="lg">
                          {isLoading ? 'Sending...' : 'Send reset link'}
                        </Button>
                      </form>
                    </Form>

                    <div className="mt-6 text-center">
                      <p className="text-sm text-muted-foreground">
                        Remember your password?{' '}
                        <Link
                          to="/auth"
                          className="font-medium text-primary hover:text-primary/80"
                        >
                          Sign in
                        </Link>
                      </p>
                    </div>
                  </CardContent>
                </>
              )}
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
